import { Application, Container, DisplayObject, Point, Ticker, Text, Graphics } from 'pixi.js';
import { AssetManager } from './AssetManager';
import { AudioManager } from './AudioManager';
import { InputBindings, InputManager } from './InputManager';
import { LayerSystem, Layer } from '@rendering/LayerSystem';
import { IsometricCamera } from '@rendering/IsometricCamera';
import { GridObject, GridSystem } from '@gameplay/building/GridSystem';
import { PlacementMode, PlacementResult, PlacementSystem, WallBuildMode } from '@gameplay/building/PlacementSystem';
import { WallSegment, WallSystem } from '@gameplay/building/WallSystem';
import { DoorSystem } from '@gameplay/building/DoorSystem';
import { FurnitureSystem, FurnitureRotation } from '@gameplay/building/FurnitureSystem';
import { UndoManager } from '@gameplay/building/UndoManager';
import { RoomManager } from '@gameplay/management/rooms/RoomManager';
import { RoomQualityCalculator } from '@gameplay/management/rooms/RoomQualityCalculator';
import { RoomCategory, RoomDefinition, ServiceLocation } from '@gameplay/management/rooms/RoomTypes';
import { ResidentManager } from '@gameplay/simulation/ResidentManager';
import { Resident } from '@gameplay/simulation/Resident';
import { FloorColorSystem } from '@gameplay/building/FloorColorSystem';
import { DebugBuilder } from '../debug/DebugBuilder';

type FloorSnapshot = {
  gridX: number;
  gridY: number;
  floor: number;
};

type WallSnapshot = {
  startX: number;
  startY: number;
  endX: number;
  endY: number;
  floor: number;
  direction: WallSegment['direction'];
};

type FurnitureSnapshot = {
  furnitureId: string;
  gridX: number;
  gridY: number;
  floor: number;
  rotation: FurnitureRotation; // Store rotation for undo/redo
};

type HistorySource = 'normal' | 'undo' | 'redo';

export class GameManager {
  private readonly app: Application;
  private readonly ticker: Ticker;

  private assetManager = AssetManager.getInstance();
  private audioManager = AudioManager.getInstance();
  private inputManager: InputManager;

  private rootContainer: Container = new Container();
  private worldContainer: Container = new Container();
  private uiContainer: Container = new Container();

  private camera: IsometricCamera;
  private gridSystem: GridSystem;
  private wallSystem: WallSystem;
  private doorSystem: DoorSystem;
  private furnitureSystem: FurnitureSystem;
  private roomManager: RoomManager;
  private roomQuality: RoomQualityCalculator;
  private residentManager: ResidentManager;
  private floorColorSystem: FloorColorSystem;
  private layers: LayerSystem;
  private placementSystem: PlacementSystem;
  private undoManager: UndoManager;
  private debugBuilder: DebugBuilder;
  private placementListeners: Array<(result: PlacementResult) => void> = [];
  private placementModeListeners: Array<(mode: PlacementMode) => void> = [];
  private roomUpdateListeners: Array<(room: RoomDefinition | undefined) => void> = [];
  private removalListeners: Array<(mode: PlacementMode, context: HistorySource, count: number) => void> = [];
  private lastPointerGrid: Point | null = null;
  private lastPointerWorld: Point | null = null;
  private isReplayingHistory = false;
  private roomCategorizationMode: RoomCategory | null = null;

  constructor(app: Application) {
    this.app = app;
    this.ticker = app.ticker;

    this.camera = new IsometricCamera(app);
    this.worldContainer = this.camera.container;
    this.layers = new LayerSystem(this.worldContainer);
    this.gridSystem = new GridSystem(this.worldContainer);
    this.wallSystem = new WallSystem();
    this.doorSystem = new DoorSystem(this.layers, this.worldContainer, this.wallSystem);
    this.furnitureSystem = new FurnitureSystem(this.gridSystem, this.layers, this.worldContainer);
    this.roomManager = new RoomManager(this.gridSystem);
    this.roomQuality = new RoomQualityCalculator(this.gridSystem);
    this.residentManager = new ResidentManager(
      this.gridSystem,
      this.wallSystem,
      this.doorSystem,
      this.roomManager,
      this.layers,
      this.worldContainer,
      {
        onResidentAdded: (resident: Resident) => {
          console.log(`🧓 Resident ${resident.stats.name} joined the manor`);
        },
        onResidentRemoved: (residentId: string) => {
          console.log(`👋 Resident left the manor`);
        },
        onResidentNeedsChanged: (resident: Resident) => {
          // Could trigger UI updates or notifications
        }
      }
    );
    this.floorColorSystem = new FloorColorSystem(app, this.gridSystem, this.roomManager);
    this.placementSystem = new PlacementSystem(
      app,
      this.gridSystem,
      this.wallSystem,
      this.furnitureSystem,
      this.doorSystem,
      this.layers,
      this.camera
    );
    this.undoManager = new UndoManager();
    this.debugBuilder = new DebugBuilder(
      this.gridSystem,
      this.wallSystem,
      this.doorSystem,
      this.placementSystem,
      this.roomManager,
      this.residentManager
    );

    this.rootContainer.addChild(this.worldContainer);
    this.rootContainer.addChild(this.uiContainer);
    this.app.stage.addChild(this.rootContainer);

    this.inputManager = InputManager.initialize(app);
    this.registerDefaultInput();
  }

  public async initialize(): Promise<void> {
    await this.preloadCoreAssets();
  }

  public start(): void {
    this.ticker.add(this.update);
  }

  public destroy(): void {
    this.ticker.remove(this.update);
    this.inputManager.dispose();
  }

  private preloadCoreAssets(): Promise<void> {
    const textures = [
      // Placeholder textures; replace with actual manifest entries when assets are available
      { name: 'floor_tile_basic', url: 'assets/sprites/buildings/floors/basic_floor.png' }
    ];

    return this.assetManager.loadTextures(textures).catch(() => Promise.resolve());
  }

  private registerDefaultInput(): void {
    const bindings: InputBindings = {
      onPointerDown: (event) => this.handlePointerDown(event),
      onPointerMove: (event) => this.handlePointerMove(event),
      onPointerUp: (event) => this.handlePointerUp(event),
      onPointerCancel: (event) => this.handlePointerCancel(event),
      onKeyDown: (event) => {
        this.handleKeyDown(event);
        this.camera.onKeyDown(event);
      },
      onKeyUp: (event) => {
        this.camera.onKeyUp(event);
      },
      onWheel: (event) => {
        // Only process wheel events if not over UI to prevent disconnection issues
        if (!this.isWheelOverUI(event)) {
          this.camera.onWheel(event);
        }
      }
    };

    this.inputManager.registerBindings(bindings);
  }

  private handlePointerDown(event: PointerEvent): void {
    // Check if the pointer is over UI elements first
    if (this.isPointerOverUI(event)) {
      return; // Don't process game input when over UI
    }
    
    const screenPoint = this.toCanvasSpace(event);
    const worldPoint = this.camera.screenToWorld(screenPoint);
    const gridPoint = this.camera.screenToGrid(screenPoint);
    this.lastPointerGrid = gridPoint.clone();
    this.lastPointerWorld = worldPoint.clone();
    
    // All drag operations now go through the placement system
    this.placementSystem.beginDrag(gridPoint);
  }

  private handlePointerMove(event: PointerEvent): void {
    try {
      // Check if the pointer is over UI elements first
      if (this.isPointerOverUI(event)) {
        return; // Don't process game input when over UI
      }
      
      const screenPoint = this.toCanvasSpace(event);
      const worldPoint = this.camera.screenToWorld(screenPoint);
      const gridPoint = this.camera.screenToGrid(screenPoint);
      this.lastPointerGrid = gridPoint.clone();
      this.lastPointerWorld = worldPoint.clone();
      
      // All drag operations now go through the placement system
      this.placementSystem.updateDrag(gridPoint);
    } catch (error) {
      console.warn('Error in handlePointerMove:', error);
      // Continue gracefully without crashing
    }
  }

  private handlePointerUp(event: PointerEvent): void {
    // Check if the pointer is over UI elements first
    if (this.isPointerOverUI(event)) {
      return; // Don't process game input when over UI
    }
    
    const screenPoint = this.toCanvasSpace(event);
    const worldPoint = this.camera.screenToWorld(screenPoint);
    const gridPoint = this.camera.screenToGrid(screenPoint);
    this.lastPointerGrid = gridPoint.clone();
    this.lastPointerWorld = worldPoint.clone();
    
    const result = this.placementSystem.endDrag(gridPoint);
    if (result) {
      // Handle room categorization results
      if (result.mode === 'room_category' && result.floors.length > 0) {
        const category = this.roomCategorizationMode; // Get current category
        if (category) {
          // Apply room category to each valid tile
          for (const tile of result.floors) {
            this.applyRoomCategoryToTile(new Point(tile.gridX, tile.gridY), category);
          }
          console.log(`🏠 Applied ${category} to ${result.floors.length} tiles`);
          // Repaint floors cohesively to avoid stale textures
          this.floorColorSystem.refreshAll();
          // Room categories may affect services/proximity
          this.recalculateAllRooms();
        }
        return;
      }
      
      // Handle normal placement results
      if (result.mode === 'floor' && result.rejectedTiles.length > 0) {
        console.info('Rejected floor tiles', result.rejectedTiles);
      }
      if (result.mode === 'wall' && result.rejectedWalls.length > 0) {
        console.info('Rejected wall edges', result.rejectedWalls);
      }
      if (result.floors.length > 0) {
        this.roomManager.recordFloorPlacement(result.floors);
        this.recalculateRoomsNear(result.floors);
      }
      result.source = 'normal';
      this.placementListeners.forEach((listener) => listener(result));
      if (!this.isReplayingHistory) {
        this.recordUndoForPlacement(result);
      }
    }
  }

  private handlePointerCancel(event: PointerEvent): void {
    event.preventDefault();
    this.placementSystem.cancelDrag();
    this.lastPointerGrid = null;
    this.lastPointerWorld = null;
  }

  private handleKeyDown(event: KeyboardEvent): void {
    if (event.key === ' ') {
      this.audioManager.playSfx('ui_click');
      return;
    }

    const key = event.key.toLowerCase();

    if (key === 'delete' || key === 'backspace') {
      event.preventDefault();
      this.handleDeleteAtPointer();
      return;
    }

    // Handle rotation for furniture and doors
    if (key === 'r') {
      const mode = this.placementSystem.getMode();
      if (mode === 'furniture' || mode === 'door') {
        event.preventDefault();
        this.placementSystem.rotateFurniture();
        this.audioManager.playSfx('ui_click'); // Provide audio feedback for rotation
        return;
      }
    }

    const hasModifier = event.ctrlKey || event.metaKey;
    if (hasModifier && key === 'z') {
      event.preventDefault();
      if (event.shiftKey) {
        this.handleRedo();
      } else {
        this.handleUndo();
      }
      return;
    }

    if (hasModifier && key === 'y') {
      event.preventDefault();
      this.handleRedo();
      return;
    }

    if (key === '1') {
      event.preventDefault();
      this.setPlacementMode('floor');
      return;
    }

    if (key === '2') {
      event.preventDefault();
      this.setPlacementMode('wall');
      return;
    }

    if (key === '3') {
      event.preventDefault();
      this.setPlacementMode('furniture');
      return;
    }

    if (key === '4') {
      event.preventDefault();
      this.setPlacementMode('door');
      return;
    }

    if (key === '5') {
      event.preventDefault();
      this.setPlacementMode('room_manager');
      return;
    }

    // Debug hotkeys
    if (key === 'f1') {
      event.preventDefault();
      this.buildDebugScenario('basic');
      return;
    }

    if (key === 'f2') {
      event.preventDefault();
      this.buildDebugScenario('minimal');
      return;
    }

    if (key === 'f3') {
      event.preventDefault();
      this.buildDebugScenario('stress');
      return;
    }
  }

  private update = (deltaTime: number): void => {
    // Update camera controls
    this.camera.update(deltaTime);
    
    // Update resident simulation
    this.residentManager.update(deltaTime);
    
    // Update other simulation systems here (placeholder)
    this.rootContainer.updateTransform();
  };

  public getLayerSystem(): LayerSystem {
    return this.layers;
  }

  public getGridSystem(): GridSystem {
    return this.gridSystem;
  }

  public getFurnitureSystem(): FurnitureSystem {
    return this.furnitureSystem;
  }

  public getPlacementSystem(): PlacementSystem {
    return this.placementSystem;
  }

  public getCamera(): IsometricCamera {
    return this.camera;
  }

  public addToLayer(displayObject: DisplayObject, layer: Layer, gridPosition: Point): void {
    this.layers.add(displayObject, layer, gridPosition);
  }

  public getUIContainer(): Container {
    return this.uiContainer;
  }

  public getResidentManager(): ResidentManager {
    return this.residentManager;
  }

  public spawnResident(position?: Point): Resident {
    const spawnPosition = position || new Point(100, 100);
    return this.residentManager.addResident(spawnPosition);
  }

  public spawnRandomResidents(count: number): void {
    this.residentManager.spawnRandomResidents(count);
  }

  public getFloorColorSystem(): FloorColorSystem {
    return this.floorColorSystem;
  }

  public toggleFloorColors(): void {
    // Room floor colors are now always enabled - no longer toggleable
  }

  public onPlacement(listener: (result: PlacementResult) => void): void {
    this.placementListeners.push(listener);
  }

  public onPlacementModeChange(listener: (mode: PlacementMode) => void): void {
    this.placementModeListeners.push(listener);
  }

  public getPlacementMode(): PlacementMode {
    return this.placementSystem.getMode();
  }

  public getRoomsManager(): RoomManager {
    return this.roomManager;
  }

  public getRoomManager(): RoomManager {
    return this.roomManager;
  }

  public getWorldContainer(): Container {
    return this.worldContainer;
  }

  public setRoomCategory(roomId: string, category: RoomCategory): void {
    const room = this.roomManager.getRoom(roomId);
    if (!room) return;

    const previousCategory = room.category;
    const updated = this.roomManager.setRoomCategory(roomId, category);
    if (updated) {
      const serviceCategories: RoomCategory[] = ['dining', 'medical', 'bathroom', 'recreation', 'utility'];
      const affectsServices =
        serviceCategories.includes(category) || serviceCategories.includes(previousCategory);

      if (affectsServices) {
        this.recalculateAllRooms();
      } else {
        this.recalculateRoom(updated);
      }
    }
  }

  public onRoomUpdated(listener: (room: RoomDefinition | undefined) => void): void {
    this.roomUpdateListeners.push(listener);
  }

  public onRemoval(listener: (mode: PlacementMode, context: HistorySource, count: number) => void): void {
    this.removalListeners.push(listener);
  }

  public getRoomServices(): Map<RoomCategory, ServiceLocation[]> {
    const map = new Map<RoomCategory, ServiceLocation[]>();
    ['dining', 'medical', 'bathroom', 'recreation', 'utility'].forEach((category) => {
      map.set(category as RoomCategory, this.roomManager.getServicesByCategory(category as RoomCategory));
    });
    return map;
  }

  public setPlacementMode(mode: PlacementMode): void {
    if (this.placementSystem.getMode() === mode) {
      return;
    }

    this.placementSystem.cancelDrag();
    this.placementSystem.setMode(mode);
    this.placementModeListeners.forEach((listener) => listener(mode));
  }

  public getWallBuildMode(): WallBuildMode {
    return this.placementSystem.getWallBuildMode();
  }

  public setWallBuildMode(mode: WallBuildMode): void {
    this.placementSystem.setWallBuildMode(mode);
  }

  public toggleWallBuildMode(): WallBuildMode {
    return this.placementSystem.toggleWallBuildMode();
  }

  public selectFurniture(furnitureId: string): void {
    this.placementSystem.setSelectedFurniture(furnitureId);
    this.setPlacementMode('furniture');
  }

  public buildDebugScenario(scenarioType: 'basic' | 'stress' | 'minimal'): void {
    console.log(`🔧 Building debug scenario: ${scenarioType}`);
    
    let scenario;
    switch (scenarioType) {
      case 'basic':
        scenario = DebugBuilder.getBasicTestScenario();
        break;
      case 'stress':
        scenario = DebugBuilder.getStressTestScenario();
        break;
      case 'minimal':
        scenario = DebugBuilder.getMinimalTestScenario();
        break;
      default:
        console.warn(`Unknown debug scenario: ${scenarioType}`);
        return;
    }
    
    this.debugBuilder.buildScenario(scenario);
    
    // Refresh room quality calculations after building
    this.recalculateAllRooms();
    
    console.log(`✅ Debug scenario '${scenario.name}' completed!`);
    console.log(`📋 Description: ${scenario.description}`);
    console.log(`🏠 Rooms: ${scenario.rooms.length}, 🪑 Furniture: ${scenario.furniture.length}, 🧓 Residents: ${scenario.residentSpawns.length}`);
  }

  private recalculateRoomsNear(objects: GridObject[]): void {
    // Recalculate quality for each affected room.
    const services = this.buildServicesMap();
    const seen = new Set<string>();

    objects.forEach((obj) => {
      const room = this.roomManager.getRoomAt(obj.gridX, obj.gridY, obj.floor);
      if (!room) {
        return;
      }

      if (seen.has(room.id)) {
        return;
      }

      seen.add(room.id);
      this.recalculateRoom(room, services);
    });
  }

  private recalculateRoom(room: RoomDefinition, services?: Map<RoomCategory, ServiceLocation[]>): void {
    const serviceMap = services ?? this.buildServicesMap();
    const quality = this.roomQuality.calculateProximityScore(room, serviceMap);
    this.roomManager.updateRoomQuality(room.id, quality);
    this.notifyRoomUpdate(room);
  }

  private buildServicesMap(): Map<RoomCategory, ServiceLocation[]> {
    const map = new Map<RoomCategory, ServiceLocation[]>();
    (['dining', 'medical', 'bathroom', 'recreation', 'utility'] as RoomCategory[]).forEach((category) => {
      map.set(category, this.roomManager.getServicesByCategory(category));
    });
    return map;
  }

  private notifyRoomUpdate(room: RoomDefinition | undefined): void {
    this.roomUpdateListeners.forEach((listener) => listener(room));
    
    // Update floor colors when room category changes
    if (room && this.floorColorSystem.enabled) {
      setTimeout(() => {
        this.floorColorSystem.updateRoomFloorColors(room);
      }, 0);
    }
  }

  private tryHandleRoomCategoryHotkey(key: string): boolean {
    // Room category hotkeys removed - use Room Manager UI instead
    return false;
  }

  private recalculateAllRooms(): void {
    const services = this.buildServicesMap();
    this.roomManager.getRooms().forEach((room) => this.recalculateRoom(room, services));
  }

  private handleDeleteAtPointer(): void {
    if (!this.lastPointerGrid) {
      return;
    }

    const mode = this.placementSystem.getMode();

    const attemptFloorRemoval = (): boolean => {
      const removed = this.placementSystem.removeFloorAt(this.lastPointerGrid!.clone());
      if (!removed) {
        return false;
      }

  this.removalListeners.forEach((listener) => listener('floor', 'normal', 1));

      const affectedRooms = this.roomManager.removeTile(removed.gridX, removed.gridY, removed.floor);

      if (affectedRooms.length === 0) {
        this.notifyRoomUpdate(undefined);
      } else {
        const services = this.buildServicesMap();
        affectedRooms.forEach((room) => this.recalculateRoom(room, services));
      }

      if (!this.isReplayingHistory) {
        this.recordUndoForFloorRemoval({
          gridX: removed.gridX,
          gridY: removed.gridY,
          floor: removed.floor
        });
      }

      return true;
    };

    const attemptWallRemoval = (): boolean => {
      if (!this.lastPointerWorld) {
        return false;
      }

      const removedWall = this.placementSystem.removeWallAt(
        this.lastPointerGrid!.clone(),
        this.lastPointerWorld.clone()
      );

      if (!removedWall) {
        return false;
      }

      this.removalListeners.forEach((listener) => listener('wall', 'normal', 1));
      if (!this.isReplayingHistory) {
        this.recordUndoForWallRemoval({
          startX: removedWall.start.x,
          startY: removedWall.start.y,
          endX: removedWall.end.x,
          endY: removedWall.end.y,
          floor: removedWall.floor,
          direction: removedWall.direction
        });
      }
      return true;
    };

    const attemptFurnitureRemoval = (): boolean => {
      const removed = this.placementSystem.removeFurnitureAt(this.lastPointerGrid!.clone());
      if (!removed) {
        return false;
      }

      this.removalListeners.forEach((listener) => listener('furniture', 'normal', 1));
      
      // Record furniture removal for undo system
      if (!this.isReplayingHistory) {
        this.recordUndoForFurnitureRemoval(removed);
      }

      return true;
    };

    if (mode === 'wall') {
      if (attemptWallRemoval()) {
        return;
      }
      attemptFloorRemoval();
      return;
    }

    if (mode === 'furniture') {
      if (attemptFurnitureRemoval()) {
        return;
      }
      // Fallback to floor removal if no furniture found
      if (attemptFloorRemoval()) {
        return;
      }
      attemptWallRemoval();
      return;
    }

    if (attemptFloorRemoval()) {
      return;
    }

    if (attemptFurnitureRemoval()) {
      return;
    }

    attemptWallRemoval();
  }

  private handleUndo(): void {
    if (this.isReplayingHistory) {
      return;
    }

    const action = this.undoManager.undo();
    if (!action) {
      return;
    }

    this.isReplayingHistory = true;
    try {
      action.undo();
    } finally {
      this.isReplayingHistory = false;
    }
  }

  private handleRedo(): void {
    if (this.isReplayingHistory) {
      return;
    }

    const action = this.undoManager.redo();
    if (!action) {
      return;
    }

    this.isReplayingHistory = true;
    try {
      action.redo();
    } finally {
      this.isReplayingHistory = false;
    }
  }

  private recordUndoForPlacement(result: PlacementResult): void {
    if (result.mode === 'floor' && result.floors.length > 0) {
      const snapshots: FloorSnapshot[] = result.floors.map((floor) => ({
        gridX: floor.gridX,
        gridY: floor.gridY,
        floor: floor.floor
      }));

      this.undoManager.push({
        mode: 'floor',
        kind: 'remove',
        undo: () => {
          const removedCount = this.removeFloorsForUndo(snapshots);
          if (removedCount > 0) {
            this.removalListeners.forEach((listener) => listener('floor', 'undo', removedCount));
          }
        },
        redo: () => {
          const restored = this.restoreFloorsForUndo(snapshots);
          if (restored.length === 0) {
            return;
          }

          const placement: PlacementResult = {
            mode: 'floor',
            floors: restored,
            walls: [],
            furniture: [],
            rejectedTiles: [],
            rejectedWalls: [],
            source: 'redo'
          };
          this.placementListeners.forEach((listener) => listener(placement));
        }
      });
      return;
    }

    if (result.mode === 'wall' && result.walls.length > 0) {
      const snapshots: WallSnapshot[] = result.walls.map((wall) => ({
        startX: wall.start.x,
        startY: wall.start.y,
        endX: wall.end.x,
        endY: wall.end.y,
        floor: wall.floor,
        direction: wall.direction
      }));

      this.undoManager.push({
        mode: 'wall',
        kind: 'remove',
        undo: () => {
          const removedCount = this.removeWallsForUndo(snapshots);
          if (removedCount > 0) {
            this.removalListeners.forEach((listener) => listener('wall', 'undo', removedCount));
          }
        },
        redo: () => {
          const restored = this.restoreWallsForUndo(snapshots);
          if (restored.length === 0) {
            return;
          }

          const placement: PlacementResult = {
            mode: 'wall',
            floors: [],
            walls: restored,
            furniture: [],
            rejectedTiles: [],
            rejectedWalls: [],
            source: 'redo'
          };
          this.placementListeners.forEach((listener) => listener(placement));
        }
      });
    }

    if (result.mode === 'furniture' && result.furniture.length > 0) {
      // We need to get the current rotation for the placed furniture
      const currentRotation = this.placementSystem.getFurnitureRotation();
      
      const snapshots: FurnitureSnapshot[] = result.furniture.map((furniture) => ({
        furnitureId: furniture.id,
        gridX: furniture.gridX,
        gridY: furniture.gridY,
        floor: furniture.floor,
        rotation: currentRotation
      }));

      this.undoManager.push({
        mode: 'furniture',
        kind: 'remove',
        undo: () => {
          const removedCount = this.removeFurnitureForUndo(snapshots);
          if (removedCount > 0) {
            this.removalListeners.forEach((listener) => listener('furniture', 'undo', removedCount));
          }
        },
        redo: () => {
          const restored = this.restoreFurnitureForUndo(snapshots);
          if (restored.length === 0) {
            return;
          }

          const placement: PlacementResult = {
            mode: 'furniture',
            floors: [],
            walls: [],
            furniture: restored,
            rejectedTiles: [],
            rejectedWalls: [],
            source: 'redo'
          };
          this.placementListeners.forEach((listener) => listener(placement));
        }
      });
    }
  }

  private recordUndoForFloorRemoval(snapshot: FloorSnapshot): void {
    this.undoManager.push({
      mode: 'floor',
      kind: 'restore',
      undo: () => {
        const restored = this.restoreFloorsForUndo([snapshot]);
        if (restored.length === 0) {
          return;
        }

        const placement: PlacementResult = {
          mode: 'floor',
          floors: restored,
          walls: [],
          furniture: [],
          rejectedTiles: [],
          rejectedWalls: [],
          source: 'undo'
        };
        this.placementListeners.forEach((listener) => listener(placement));
      },
      redo: () => {
        const removedCount = this.removeFloorsForUndo([snapshot]);
        if (removedCount > 0) {
          this.removalListeners.forEach((listener) => listener('floor', 'redo', removedCount));
        }
      }
    });
  }

  private recordUndoForWallRemoval(snapshot: WallSnapshot): void {
    this.undoManager.push({
      mode: 'wall',
      kind: 'restore',
      undo: () => {
        const restored = this.restoreWallsForUndo([snapshot]);
        if (restored.length === 0) {
          return;
        }

        const placement: PlacementResult = {
          mode: 'wall',
          floors: [],
          walls: restored,
          furniture: [],
          rejectedTiles: [],
          rejectedWalls: [],
          source: 'undo'
        };
        this.placementListeners.forEach((listener) => listener(placement));
      },
      redo: () => {
        const removedCount = this.removeWallsForUndo([snapshot]);
        if (removedCount > 0) {
          this.removalListeners.forEach((listener) => listener('wall', 'redo', removedCount));
        }
      }
    });
  }

  private recordUndoForFurnitureRemoval(removed: GridObject): void {
    // Note: Currently we assume default rotation since GridObject doesn't store rotation
    // This could be enhanced later to track rotation more precisely
    const snapshot: FurnitureSnapshot = {
      furnitureId: removed.id,
      gridX: removed.gridX,
      gridY: removed.gridY,
      floor: removed.floor,
      rotation: 0 // Default rotation assumption
    };

    this.undoManager.push({
      mode: 'furniture',
      kind: 'restore',
      undo: () => {
        const restored = this.restoreFurnitureForUndo([snapshot]);
        if (restored.length === 0) {
          return;
        }

        const placement: PlacementResult = {
          mode: 'furniture',
          floors: [],
          walls: [],
          furniture: restored,
          rejectedTiles: [],
          rejectedWalls: [],
          source: 'undo'
        };
        this.placementListeners.forEach((listener) => listener(placement));
      },
      redo: () => {
        const removedCount = this.removeFurnitureForUndo([snapshot]);
        if (removedCount > 0) {
          this.removalListeners.forEach((listener) => listener('furniture', 'redo', removedCount));
        }
      }
    });
  }

  private removeFloorsForUndo(targets: FloorSnapshot[]): number {
    const affectedRooms = new Set<string>();
    const roomsToUpdate: RoomDefinition[] = [];
    let removedCount = 0;

    targets.forEach((target) => {
      const removed = this.placementSystem.removeFloorAt(new Point(target.gridX, target.gridY), target.floor);
      if (!removed) {
        return;
      }

      removedCount += 1;

      const impacted = this.roomManager.removeTile(target.gridX, target.gridY, target.floor);
      impacted.forEach((room) => {
        if (!affectedRooms.has(room.id)) {
          affectedRooms.add(room.id);
          roomsToUpdate.push(room);
        }
      });
    });

    if (removedCount === 0) {
      return 0;
    }

    if (roomsToUpdate.length === 0) {
      this.notifyRoomUpdate(undefined);
      return removedCount;
    }

    const services = this.buildServicesMap();
    roomsToUpdate.forEach((room) => this.recalculateRoom(room, services));
    return removedCount;
  }

  private restoreFloorsForUndo(targets: FloorSnapshot[]): GridObject[] {
    const restored: GridObject[] = [];

    targets.forEach((target) => {
      if (this.gridSystem.isOccupied(target.gridX, target.gridY, target.floor)) {
        return;
      }

      const floorObject = this.placementSystem.placeFloor(new Point(target.gridX, target.gridY), target.floor);
      restored.push(floorObject);
    });

    if (restored.length > 0) {
      this.roomManager.recordFloorPlacement(restored);
      this.recalculateRoomsNear(restored);
    }

    return restored;
  }

  private removeWallsForUndo(targets: WallSnapshot[]): number {
    let removedCount = 0;

    targets.forEach((target) => {
      const removed = this.wallSystem.removeSegment(
        target.startX,
        target.startY,
        target.endX,
        target.endY,
        target.floor
      );

      if (!removed) {
        return;
      }

      removedCount += 1;

      if (removed.graphic) {
        removed.graphic.parent?.removeChild(removed.graphic);
        removed.graphic.destroy();
        removed.graphic = undefined;
      }
    });

    return removedCount;
  }

  private restoreWallsForUndo(targets: WallSnapshot[]): WallSegment[] {
    const restored: WallSegment[] = [];

    targets.forEach((target) => {
      const segment = this.placementSystem.restoreWallSegment({
        id: '',
        type: 'wall',
        floor: target.floor,
        direction: target.direction,
        start: new Point(target.startX, target.startY),
        end: new Point(target.endX, target.endY)
      });
      restored.push(segment);
    });

    return restored;
  }

  private removeFurnitureForUndo(targets: FurnitureSnapshot[]): number {
    let removedCount = 0;

    targets.forEach((target) => {
      const removed = this.furnitureSystem.removeFurniture(target.gridX, target.gridY, target.floor);
      if (removed) {
        removedCount += 1;
      }
    });

    return removedCount;
  }

  private restoreFurnitureForUndo(targets: FurnitureSnapshot[]): GridObject[] {
    const restored: GridObject[] = [];

    targets.forEach((target) => {
      console.log('🔄 Attempting to restore furniture:', target);

      // Get furniture definition to check dimensions
      const definition = this.furnitureSystem.getFurnitureDefinition(target.furnitureId);
      if (!definition) {
        console.log('❌ No furniture definition found for:', target.furnitureId);
        return;
      }

      console.log('✅ Furniture definition found, attempting placement...');

      // Place the furniture directly - let FurnitureSystem handle all validation
      const furnitureObject = this.furnitureSystem.placeFurniture(
        target.furnitureId,
        target.gridX,
        target.gridY,
        target.floor,
        target.rotation
      );
      
      if (furnitureObject) {
        console.log('✅ Furniture object created:', furnitureObject);
        console.log('🖼️ Sprite reference:', furnitureObject.sprite ? 'Present' : 'Missing');
        restored.push(furnitureObject);
      } else {
        console.log('❌ Failed to create furniture object - FurnitureSystem validation failed');
      }
    });

    console.log('🔄 Restoration complete. Restored count:', restored.length);
    return restored;
  }

  private toCanvasSpace(event: PointerEvent): Point {
    const canvas = this.app.view as HTMLCanvasElement;
    const bounds = canvas.getBoundingClientRect();
    const x = event.clientX - bounds.left;
    const y = event.clientY - bounds.top;
    return new Point(x, y);
  }

  private isPointerOverUI(event: PointerEvent): boolean {
    const screenPoint = this.toCanvasSpace(event);
    
    // Check if pointer is over any visible UI children
    return this.checkUIBounds(this.uiContainer, screenPoint);
  }

  private isWheelOverUI(event: WheelEvent): boolean {
    // Convert wheel event to screen point using similar logic
    const canvas = this.app.view as HTMLCanvasElement;
    const rect = canvas.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    const screenPoint = new Point(x, y);
    
    // Check if wheel event is over any visible UI children
    return this.checkUIBounds(this.uiContainer, screenPoint);
  }

  private checkUIBounds(container: Container, point: Point): boolean {
    if (!container.visible || container.alpha <= 0) return false;
    
    // Check each child individually - don't trust container bounds as they can be misleading
    for (const child of container.children) {
      if (!child.visible || child.alpha <= 0) continue;
      
      // Get the local bounds and check if they're meaningful
      const bounds = child.getBounds();
      if (bounds.width <= 0 || bounds.height <= 0) {
        // Recursively check containers even with no bounds
        if (child instanceof Container) {
          if (this.checkUIBounds(child, point)) {
            return true;
          }
        }
        continue;
      }
      
      // Check if point is within this child's bounds
      if (point.x >= bounds.x && point.x <= bounds.x + bounds.width &&
          point.y >= bounds.y && point.y <= bounds.y + bounds.height) {
        
        // Special handling for different UI element types
        
        // Skip non-interactive text (labels, status text, etc.)
        if (child instanceof Text && !child.interactive) {
          continue; // Allow clicks through informational text
        }
        
        // Interactive elements should always block
        if (child.interactive) {
          return true;
        }
        
        // Graphics elements (panels, backgrounds) - check if they should block
        if (child instanceof Graphics) {
          // Only block for graphics that are likely UI panels
          // Small graphics (< 50px) or very large graphics (> 800px width) are likely backgrounds/decorations
          if (bounds.width > 50 && bounds.width < 800 && bounds.height > 20) {
            return true;
          }
          // Skip very large or very small graphics
          continue;
        }
        
        // For containers, recursively check their children
        if (child instanceof Container) {
          if (this.checkUIBounds(child, point)) {
            return true;
          }
          // If container has no blocking children, don't block for the container itself
          continue;
        }
        
        // Default case for other display objects (sprites, etc.) - block if interactive
        if (child.interactive) {
          return true;
        }
      }
    }
    
    return false;
  }

  private handleRoomCategorization(gridPoint: Point): void {
    if (!this.roomCategorizationMode) return;
    
    // Find the room at this grid position
    const room = this.roomManager.getRoomAt(gridPoint.x, gridPoint.y, 0);
    
    if (room) {
      console.log(`🏠 Applying ${this.roomCategorizationMode} to room ${room.id}`);
      this.setRoomCategory(room.id, this.roomCategorizationMode);
      
      // Exit categorization mode after successful application
      this.exitRoomCategorizationMode();
    } else {
      console.log('❌ No room found at clicked position');
    }
  }

  public enterRoomCategorizationMode(category: RoomCategory): void {
    this.roomCategorizationMode = category;
    // Ensure placement system has no active drag/preview while categorizing
    this.placementSystem.cancelDrag();
    // Set placement system to room category mode
    this.placementSystem.setMode('room_category');
    this.placementSystem.setRoomCategory(category);
    console.log(`🏠 Entered room categorization mode for: ${category}`);
    
    // TODO: Show visual feedback (cursor change, highlighting, etc.)
  }

  public exitRoomCategorizationMode(): void {
    this.roomCategorizationMode = null;
    // Return placement system to floor mode
    this.placementSystem.setMode('floor');
    this.placementSystem.setRoomCategory(null);
    console.log(`🏠 Exited room categorization mode`);
    
    // TODO: Remove visual feedback
  }

  public isInRoomCategorizationMode(): boolean {
    return this.roomCategorizationMode !== null;
  }

  private applyRoomCategoryToTile(tile: Point, category: RoomCategory): void {
    // Apply category at tile-level (splitting rooms as needed)
    this.roomManager.setTileCategory(tile.x, tile.y, 0, category);
    // Update floor colors to reflect change immediately
    this.floorColorSystem.updateFloorTile(tile.x, tile.y, 0);
  }
}
